import { Content } from '@forchange/storytime';
import { DirectiveOptions } from 'vue';
import { Vue } from 'vue-property-decorator';
import SelectMenu from './index.vue';

export { default } from './index.vue';

let selectMenu: Vue | undefined;
let vm: Vue | undefined;

export type SystemData = {
  version: string;
  courseId: string;
  type: 'text' | 'image' | 'video';
  utteranceIndex: number;
};

type SelectMenuType = Vue & {
  content: Content;
  messageHeight: number;
  position: {
    top: number;
    left: number;
  };
  changeDate: number;
  system: SystemData;
};

let data: SelectMenuType | undefined;

const getElementPagePosition = (element: HTMLElement | null) => {
  if (!element) return;
  let actualTop = element.offsetTop;
  let actualLeft = element.offsetLeft;
  let current = element.offsetParent as HTMLElement;
  while (current !== null) {
    actualTop += current.offsetTop + current.clientTop;
    actualLeft += current.offsetLeft + current.clientLeft;
    current = current?.offsetParent as HTMLElement;
  }

  const dialog_scroller = document.body.querySelector('#dialog-scroller');
  if (dialog_scroller) {
    actualTop -= dialog_scroller.scrollTop;
  }
  return {
    top: actualTop,
    left: actualLeft
  };
};

type MenuStyle = {
  top: number;
  left: number;
};

function updateMenu(
  this: any,
  menuStyle: MenuStyle,
  content: Content,
  positionParams: { messageHeight: number }
) {
  if (!data) {
    initData(menuStyle, content, positionParams, {
      version: this.version,
      courseId: this.courseId,
      type: this.type,
      utteranceIndex: this.utteranceIndex
    });
  }
  if (!selectMenu && data) {
    return initMenu(data);
  }

  // update data
  if (data) {
    data.content = content;
    data.position = menuStyle;
    data.messageHeight = positionParams.messageHeight || 0;
    data.changeDate = new Date().getTime();
    data.system = {
      version: this.version,
      courseId: this.courseId,
      type: this.type,
      utteranceIndex: this.utteranceIndex
    };

    changeData(data);
  }
}

function initMenu(data: SelectMenuType) {
  selectMenu = new SelectMenu().$mount();
  changeData(data);
  document.body.appendChild(selectMenu.$el);

  selectMenu.$on('addSuccess', () => {
    vm?.$store.dispatch('note/resetList');
  });
}

function changeData(data: SelectMenuType) {
  selectMenu?.$set(selectMenu, 'content', data.content);
  selectMenu?.$set(selectMenu, 'messageHeight', data.messageHeight);
  selectMenu?.$set(selectMenu, 'top', data.position.top);
  selectMenu?.$set(selectMenu, 'left', data.position.left);
  selectMenu?.$set(selectMenu, 'changeDate', data.changeDate);
  selectMenu?.$set(selectMenu, 'system', data.system);
}

function initData(
  menuStyle: MenuStyle,
  content: Content,
  positionParams: { messageHeight: number },
  system: SystemData
) {
  data = new Vue({
    data: {
      content: content,
      messageHeight: positionParams.messageHeight,
      position: {
        top: menuStyle.top,
        left: menuStyle.left
      },
      changeDate: new Date().getTime(),
      system
    }
  });
}

function positionMenu(
  this: { content: Content; utteranceIndex: number; el: HTMLElement | null },
  e?: MouseEvent
) {
  const position = getPosition(this.el, e);
  if (!position) return;
  updateMenu.bind(this)(
    {
      ...position
    },
    this.content,
    {
      messageHeight: this.el?.offsetHeight || 0
    }
  );
}

const getPosition = (el: HTMLElement | null, e?: MouseEvent): MenuStyle => {
  let position = {
    top: -9999,
    left: -9999
  };
  if (e) {
    const pagePosition = getElementPagePosition(el);
    if (pagePosition) position = pagePosition;

    position.left = e.clientX;
  }

  return position;
};

const clearSelectMenu = () => {
  (selectMenu as Vue & { close: Function })?.close();
};

export const directive: DirectiveOptions = {
  bind(el, binding, vnode) {
    const props = vnode.context?.$options.propsData as {
      content: Content;
      el: HTMLElement | null;
      version: string;
      courseId: string;
      type: 'text' | 'image' | 'video';
      utteranceIndex: number;
    };
    setTimeout(() => {
      const common = vnode.context?.$store.state.common;
      props.el = vnode.elm?.parentElement || null;
      props.version = common?.version || '';
      props.courseId = common.environment?.sharedKey || '';
      props.type = binding.value.type || 'text';
      // 发言使用
      props.utteranceIndex = binding.value.index;
      // 初始化
      if (!selectMenu) positionMenu.bind(props)();
      // 条件触发
      if (
        !(
          vnode.context?.$route?.query?.type === 'preview' &&
          vnode.context?.$route?.path.startsWith('/main') &&
          vnode.context?.$route?.params.pathMatch.startsWith('https')
        )
      ) {
        el.addEventListener('click', positionMenu.bind({ ...props }));
      }

      // clear
      const dialog_scroller = document.body.querySelector('#dialog-scroller');
      dialog_scroller?.addEventListener('scroll', clearSelectMenu);
      window.addEventListener('resize', clearSelectMenu);
    });

    vm = vnode.context;
  },
  unbind(_el) {
    const dialog_scroller = document.body.querySelector('#dialog-scroller');
    dialog_scroller?.removeEventListener('scroll', clearSelectMenu);
    window.removeEventListener('resize', clearSelectMenu);
  }
};
